// components/GroupMemoryCard.jsx — Anamoria SPA
// v1.0 — NEW-163 Increment B (2026-10-30): the RESPONSE-CARD treatment for a group member's memory
//   that answers the leader's note. Rebuilt, scoped, from mockup v0.8 (header) + v0.11 (glyphs) after
//   the feed-wide v0.3 was reverted (#216) — the three regressions it must not repeat are pinned in
//   GroupMemoryCard.test.jsx (item-3 log v1.6 §2; plan v0.5 §4.2; spec v0.2 §4).
//
//   RENDER RULE (the fix for the revert): a memory gets this treatment iff
//       memory.groupNoteId && spaceIsGroup
//   — `isResponseCard()`. Never feed-wide; never in a private space (the guard returns false when
//   !spaceIsGroup even if groupNoteId is somehow present). Everything else → the legacy card.
//
//   This component is the HEADER + "in response to" line only. The body by type (voice / video / photo /
//   text) is the existing card body — ContributorMemoryFeed v1.10 (member) and MemoryFeed v2.47 (leader)
//   render <GroupMemoryCard> at the top of their existing card chrome; media rendering is not re-implemented.
//     header: avatar (initial), member name, "remembering {lovedOneName}" when set.
//     footer (Sophie, live render 2026-10-30 — the card's existing footer, where "{name} · 4h ago" sat):
//             left <ResponseDay> = gentle day, capitalised ("Today"); right <ResponseChip> = MediaTypeChip,
//             just before the privacy icon / sharing-state pill. The name is not repeated.
//     "in response to" line (copy v1.0, resolved client-side from groupNotes — `responsePillText()`):
//       B-6a  groupNoteId === groupNotes.current.id      → "In response to {leader}'s note"
//       B-6b  found in groupNotes.earlier[]              → "In response to {leader}'s note from {day}"
//       B-6c  not found (older than the kept list)       → "In response to an earlier note"
//   Presentational; no fetch, no behaviour.
import MediaTypeChip from './MediaTypeChip';
import { noteDayLabel, firstName } from './GroupNoteBanner';
import { findGroupNote } from '../utils/contributorCapture';
import styles from './GroupMemoryCard.module.css';

/** The render rule. Defence in depth: false whenever the space is not a group space. */
export function isResponseCard(memory, spaceIsGroup) {
  return spaceIsGroup === true && Boolean(memory && memory.groupNoteId);
}

/** B-6a / B-6b / B-6c. `leaderName` is the fallback when the note carries no authorName. */
export function responsePillText(groupNoteId, groupNotes, leaderName) {
  const hit = findGroupNote(groupNotes, groupNoteId);
  if (!hit) return 'In response to an earlier note';
  const who = firstName(hit.note.authorName || leaderName);
  if (hit.isCurrent) return `In response to ${who}'s note`;
  const day = noteDayLabel(hit.note.createdAt);
  return day ? `In response to ${who}'s note from ${day}` : `In response to ${who}'s note`;
}

/** `insetRight`: px of clear space on the right of the header row, for a caller whose card chrome
 *  overlays a control there (ContributorMemoryFeed's own-card pencil, 32px at top/right 8px). */
/** Footer, left: the gentle day, capitalised ("Today", "Yesterday", "Friday", "12 Oct"). Replaces the legacy
 *  "{name} · {date}" attribution, since the header already names the member. */
export function ResponseDay({ memory }) {
  const d = noteDayLabel(memory?.createdAt);
  if (!d) return null;
  return <span className={styles.day} data-testid="response-card-day">{d.charAt(0).toUpperCase() + d.slice(1)}</span>;
}

/** Footer, right: the type chip, placed just before the privacy icon (leader) / sharing-state pill (member). */
export function ResponseChip({ memory, category }) {
  return <MediaTypeChip category={(category || memory?.category || '').toLowerCase()} />;
}

/** `padX`: side padding to match the host card's content padding (14 legacy, 20 in the redesigned leader feed). */
export default function GroupMemoryCard({ memory, groupNotes, leaderName, insetRight = 0, padX = 14 }) {
  const name = (memory?.creatorName || '').trim();
  const initial = (name[0] || '?').toUpperCase();
  const remembering = (memory?.lovedOneName || '').trim();
  return (
    <div className={styles.wrap} style={padX !== 14 ? { paddingLeft: padX, paddingRight: padX } : undefined} data-testid="response-card-header">
      <div className={styles.head} style={insetRight ? { paddingRight: insetRight } : undefined}>
        <span className={styles.avatar} aria-hidden="true">{initial}</span>
        <div className={styles.whoWrap}>
          {name && <div className={styles.who}>{name}</div>}
          {remembering && <div className={styles.remembering}>remembering {remembering}</div>}
        </div>
      </div>
      <span className={styles.pill}>{responsePillText(memory?.groupNoteId, groupNotes, leaderName)}</span>
    </div>
  );
}
